import VisualIcon from "@icons/image/image-purple.svg";
import { BACKGROUND_IMAGE } from "@/app/(protected)/background-generation/components/BackgroundInputs/FieldConstants";
import { BackgroundInptSchema } from "./components/BackgroundInputs/schema";
import { PROJECT_TYPES, STAGE_NAMES, VALID_GTM_EVENT_NAMES } from "@/constants/constants";
import { handleGenerateConcepts, handleGenerateOutputRequest, patchProject, checkRaiGeneratedOutputs } from "@/app/actions/actions";
import { normalizeBrandId, StepsDataProps, getTimeDifference } from "@/lib/utils";
import { FormValues, GAEventHandler, RAIResponse } from "@/types";
import { getGTMEventData } from "@/app/(protected)/asset-generation/components/ExploreOptions/utils";
import {
  ConceptGenerationResponse,
  PatchProjectWithConceptResponse,
  GenerateBackgroundOutputsResponse,
  PerformImageRaiCheckResponse,
} from "./types/backgroundGenerationHelpers";
import { BackgroundInputsPayload, HydrateBackgroundGenerationPayload } from "./types";
import { ModelOutputs } from "@/app/(protected)/concepting/components/StepFour/types";
import { IMAGE } from "../saved-projects/constants";
import { useBackgroundGenerationStore } from "./store/useBackgroundGenerationStore";
import { getImageDimensionRatio } from "../asset-generation/components/ExploreOptions/utils";
import { AssetTypeEnum } from "../asset-generation/components/ReviewAssets/components/constants";
import { FieldErrors } from "react-hook-form";

export function buildHydrateBackgroundPayload(args: {
  projectName: string;
  projectId: string;
  imageDimensions: string;
  noOfImages: string;
  channel: string;
  modelOutputs: ModelOutputs;
  raiData: RAIResponse[];
  isSavedProject: boolean;
  version: number;
  brandId: string;
  setWorkflowUtils?: React.Dispatch<React.SetStateAction<StepsDataProps>>;
}): HydrateBackgroundGenerationPayload {
  const {
    projectName,
    projectId,
    imageDimensions,
    noOfImages,
    channel,
    modelOutputs,
    raiData,
    isSavedProject,
    version,
    brandId,
    setWorkflowUtils,
  } = args;

  return {
    projectName,
    projectId,
    imageDimensions,
    noOfImages,
    channel,
    modelOutputs,
    raiData,
    isSavedProject,
    version,
    brandId,
    setWorkflowUtils,
  };
}

export const getAssestData = (noOfImages: string, imageDimensions: string, channel: string) => [
  { icon: VisualIcon, text: BACKGROUND_IMAGE, value: noOfImages },
  { text: imageDimensions },
  ...(channel ? [{ text: channel }] : []),
];

export const getPayload = (data: BackgroundInptSchema) => ({
  imageDescription: data.backgroundImageDescription,
  stage: STAGE_NAMES.BACKGROUND_PROJECT_OPTIONS,
});

export const generateConcepts = async (description: string, brandId: string, projectId: string): Promise<ConceptGenerationResponse> => {
  const conceptsResponse = await handleGenerateConcepts(
    {
      inputImageDescription: description,
      brandId: normalizeBrandId(brandId),
      projectType: PROJECT_TYPES.BACKGROUND,
      imageType: PROJECT_TYPES.BACKGROUND,
    },
    projectId,
  );

  return conceptsResponse as ConceptGenerationResponse;
};

export const patchProjectWithConcept = async (
  description: string,
  conceptId: string,
  projectId: string,
  cachedRaiResponse?: RAIResponse[] | null,
): Promise<PatchProjectWithConceptResponse> => {
  const patchPayload: BackgroundInputsPayload = {
    stage: STAGE_NAMES.ENTER_DESCRIPTION,
    description,
    selectedConcept: { conceptId },
    // Include RAI check data for description if available
    raiChecks: cachedRaiResponse && cachedRaiResponse.length > 0 ? [{ description: cachedRaiResponse[0] }] : [],
  };

  const patchResponse = await patchProject(PROJECT_TYPES.BACKGROUND, projectId, patchPayload);

  return {
    success: patchResponse.success,
    response: patchResponse.response,
    error: patchResponse.error,
  };
};

export const generateBackgroundOutputs = async (
  brandKb: string,
  channelKb: string | undefined,
  projectId: string,
  options?: { noOfOutputs?: number; useExistingPrompt?: boolean; inputImagePath?: string },
): Promise<GenerateBackgroundOutputsResponse> => {
  const outputRequestBody = {
    brandKb: JSON.parse(brandKb),
    ...(channelKb && { channelKb: JSON.parse(channelKb) }),
    ...(options?.noOfOutputs && { noOfOutputs: options.noOfOutputs }),
    ...(options?.useExistingPrompt && { useExistingPrompt: true }),
    ...(options?.inputImagePath && { inputImagePath: options.inputImagePath }),
  };

  const raw = await handleGenerateOutputRequest(PROJECT_TYPES.BACKGROUND, projectId, outputRequestBody, "v3");

  return raw;
};

export const performImageRaiCheck = async (modelOutputs: ModelOutputs, projectId: string): Promise<PerformImageRaiCheckResponse | null> => {
  const firstImage = modelOutputs.imageGroup?.[0] || modelOutputs.image;
  const firstImageUrl = firstImage?.jpegURL;

  if (!firstImageUrl) return null;

  const raiCheckResponse = await checkRaiGeneratedOutputs(PROJECT_TYPES.BACKGROUND, projectId, [
    { assetType: IMAGE, assetUrl: firstImageUrl },
  ]);

  return raiCheckResponse;
};

export const hydrateBackgroundGeneration = (payload: HydrateBackgroundGenerationPayload): void => {
  const {
    projectName,
    projectId,
    imageDimensions,
    noOfImages,
    channel,
    modelOutputs,
    raiData,
    isSavedProject,
    setWorkflowUtils,
    version,
    brandId,
  } = payload;

  useBackgroundGenerationStore.setState({
    projectName,
    projectId,
    imageDimensions,
    noOfImages,
    channel: { id: channel, label: channel },
    isSaveProjects: isSavedProject,
    backgroundReview: { modelOutputs: modelOutputs, cachedOutputRaiResponse: raiData },
    version: version,
    brandId: brandId,
  });

  setWorkflowUtils?.((prevState) => {
    const updatedState = { ...prevState };

    Object.assign(updatedState, {
      projectId: projectId,
      projectType: PROJECT_TYPES.BACKGROUND,
      isSavedProject: isSavedProject,
      selectedAspectRatio: getImageDimensionRatio(imageDimensions),
      brandId: brandId,
      version: version,
    });

    if (!updatedState.stepOneData) updatedState.stepOneData = {} as typeof updatedState.stepOneData;
    if (!updatedState.stepOneData.backgroundGeneration)
      updatedState.stepOneData.backgroundGeneration = {} as typeof updatedState.stepOneData.backgroundGeneration;
    updatedState.stepOneData.backgroundGeneration.numberOfImages = noOfImages;

    Object.assign(updatedState.stepOneData.backgroundGeneration, {
      assetType: AssetTypeEnum.Visual,
      projectName: projectName,
      copyOutput: { label: "" },
      isSavedProject: isSavedProject,
      selectedAspectRatio: getImageDimensionRatio(imageDimensions),
    });

    const stepThreeBackgroundGen = updatedState.stepThreeData.backgroundGeneration as unknown as Record<string, unknown>;
    stepThreeBackgroundGen.outputId = "";

    if (raiData) {
      stepThreeBackgroundGen.raiOutputsCheck = {
        data: {
          image: raiData[0] ?? [raiData],
          images: raiData,
        },
      };
    }
    return updatedState;
  });
};

export function handleFormAnalytics<TFieldValues extends FormValues>(
  eventData: {
    stepName: string;
    previousTimeStamp: number;
    errors?: FieldErrors<TFieldValues>;
    errorMessage?: string;
    linkSection?: string;
    formValues?: TFieldValues;
    linkText?: string;
    pageName?: string;
    copyOutput?: string;
  },
  handleGenericGAEvent: GAEventHandler,
): void {
  const {
    stepName,
    errors = {},
    formValues,
    previousTimeStamp,
    linkSection,
    linkText,
    errorMessage,
    pageName,
    copyOutput = "",
  } = eventData;

  const analyticsEventData = {
    ...getGTMEventData({
      stepName,
      pageName,
      errors,
      linkSection,
      data: {
        ...formValues,
        title: stepName,
        assetType: IMAGE,
      },
      timeTaken: getTimeDifference(previousTimeStamp),
    }),
    ...(linkSection ? { linkSection } : {}),
    copyOutput: copyOutput,
  };
  const finalEventData = formValues
    ? analyticsEventData
    : {
        timeTaken: getTimeDifference(previousTimeStamp),
        assetType: IMAGE,
        errorMessage: errorMessage,
        linkSection,
        linkText,
        copyOutput,
      };

  handleGenericGAEvent(VALID_GTM_EVENT_NAMES.CONTINUE, finalEventData);
}
